import { useTranslation } from "react-i18next";
import type { Document } from "../../types/document";
import styles from "./DocumentList.module.css";

interface DocumentListProps {
	documents: Document[];
	onDocumentClick: (document: Document) => void;
}

export function DocumentList({
	documents,
	onDocumentClick,
}: DocumentListProps) {
	const { t } = useTranslation("documents");

	const getFileIcon = (mimeType: string): string => {
		if (mimeType.startsWith("image/")) return "pi-image";
		if (mimeType === "application/pdf") return "pi-file-pdf";
		if (
			mimeType === "application/msword" ||
			mimeType ===
				"application/vnd.openxmlformats-officedocument.wordprocessingml.document"
		)
			return "pi-file-word";
		return "pi-file";
	};

	const formatDate = (dateString: string): string => {
		const date = new Date(dateString);
		return date.toLocaleDateString(undefined, {
			year: "numeric",
			month: "short",
			day: "numeric",
		});
	};

	const formatFileSize = (bytes: number): string => {
		if (bytes < 1024) return `${bytes} B`;
		if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
		return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
	};

	return (
		<div className={styles.list}>
			<div className={styles.header}>
				<div className={styles.headerCell}>{t("list.name")}</div>
				<div className={styles.headerCell}>{t("list.category")}</div>
				<div className={styles.headerCell}>{t("list.date")}</div>
				<div className={styles.headerCell}>{t("list.size")}</div>
				<div className={styles.headerCell}>{t("list.shared")}</div>
			</div>

			{documents.map((document) => (
				<button
					key={document.id}
					type="button"
					className={styles.row}
					onClick={() => onDocumentClick(document)}
				>
					<div className={styles.cell}>
						<i
							className={`pi ${getFileIcon(document.mimeType)} ${styles.icon}`}
						/>
						<div className={styles.nameWrapper}>
							<span className={styles.name}>{document.name}</span>
							{document.description && (
								<span className={styles.description}>
									{document.description}
								</span>
							)}
						</div>
					</div>

					<div className={styles.cell}>
						<span className={styles.category}>
							{t(`categories.${document.category}`)}
						</span>
					</div>

					<div className={styles.cell}>
						<span className={styles.date}>
							{formatDate(document.uploadedAt)}
						</span>
					</div>

					<div className={styles.cell}>
						<span className={styles.size}>
							{formatFileSize(document.fileSize)}
						</span>
					</div>

					<div className={styles.cell}>
						{document.shareCount > 0 ? (
							<span className={styles.shared}>
								<i className="pi pi-users" />
								{document.shareCount}
							</span>
						) : (
							<span className={styles.notShared}>—</span>
						)}
					</div>
				</button>
			))}
		</div>
	);
}
